import React, {useMemo, useRef, useState, useEffect} from 'react'
import {CampaignDetailResponse, FormField} from '../core/_models'
import {
  importLeads, getSubmissionsByCampaign, saveOutboundCall, triggerCallInitiate,
  getWelcomeStatusCall, getWelcomeResultCall, getWelcomeStatus, getWelcomeProductKnowledge
} from '../core/_requests'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'
import clsx from 'clsx'
import { toast } from 'react-toastify'

type Props = {
  data: CampaignDetailResponse 
  externalTicketTrigger?: {phone: string, timestamp: number} | null
  onTriggerProcessed?: () => void
}

export const CampaignDetailInfo = ({data, externalTicketTrigger, onTriggerProcessed}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importPreview, setImportPreview] = useState<any[]>([])
  const [realData, setRealData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalData, setTotalData] = useState(0)
  const [formField, setFormField] = useState([])
  const pageSize = 10

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<any | null>(null)
  const [callNote, setCallNote] = useState('')

  const [welcomeStatusCall, setWelcomeStatusCall] = useState<Array<{ tablecode: string }>>([])
  const [welcomeResultCall, setWelcomeResultCall] = useState<Array<{ tablecode: string }>>([])
  const [welcomeStatus, setWelcomeStatus] = useState<Array<{ tablecode: string }>>([])
  const [welcomeProductKnowledge, setWelcomeProductKnowledge] = useState<Array<{ tablecode: string }>>([])
  
  const [valWelcomeStatusCall, setValWelcomeStatusCall] = useState('')
  const [valWelcomeResultCall, setValWelcomeResultCall] = useState('')
  const [valWelcomeStatus, setValWelcomeStatus] = useState('')
  const [valWelcomeProductKnowledge, setValWelcomeProductKnowledge] = useState('')
  const [isLoadingStats, setIsLoadingStats] = useState(true)

  const [iconColorClass, setIconColorClass] = useState('text-primary')
  
  useEffect(() => {
    const hour = new Date().getHours()
    if (hour >= 18 || hour < 6) {
      setIconColorClass('text-warning')
    } else {
      setIconColorClass('text-primary')
    }
  }, [])

  const fetchInitialData = async () => {
    try {
      const [wscRes, wrcRes, wsRes, wpkRes] = await Promise.all([
          getWelcomeStatusCall(),
          getWelcomeResultCall(),
          getWelcomeStatus(),
          getWelcomeProductKnowledge()
      ])
      setWelcomeStatusCall(wscRes.data)
      setWelcomeResultCall(wrcRes.data)
      setWelcomeStatus(wsRes.data)
      setWelcomeProductKnowledge(wpkRes.data)
    } catch {
      toast.error('Failed to fetch initial data')
    } finally {
      setIsLoadingStats(false)
    }
  }
  
  const fields: FormField[] = data?.formFields ?? []

  useEffect(() => {
    if (externalTicketTrigger && realData.length > 0) {
      const matchingLead = realData.find(l => 
        l.phone_number?.includes(externalTicketTrigger.phone) || 
        externalTicketTrigger.phone.includes(l.phone_number)
      )
      if (matchingLead) {
        setSelectedLead(matchingLead)
        setIsDrawerOpen(true)
        if (onTriggerProcessed) onTriggerProcessed()
      }
    }
  }, [externalTicketTrigger, realData, onTriggerProcessed])

  const globalCanCall = useMemo(() => fields.some((f) => f.canCall === true), [fields])

  const fetchLeads = async (page: number = 1) => {
    if (!data.id) return
    setLoading(true)
    try {
      const response = await getSubmissionsByCampaign(data.id as string, `page=${page}&items_per_page=${pageSize}`)
      setRealData(response.data || [])
      setTotalData(response.total || 0)
      setFormField(response.formField)
      setCurrentPage(page)
    } catch (e) {
      console.error("Gagal fetch leads:", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLeads(1)
    fetchInitialData()
  }, [data.id])

  const totalPages = Math.ceil(totalData / pageSize)

  const handleExportTemplate = () => {
    if (fields.length === 0) return Swal.fire('Error', 'Field belum dikonfigurasi', 'error')
    const headers = [[...fields.map((f) => f.fieldName || '')]]
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(headers)
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, `Template_Leads_${data.name.replace(/\s+/g, '_')}.xlsx`)
  }

  const handleImportLeads = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result
        const wb = XLSX.read(bstr, {type: 'binary'})
        const dataJson: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
        setImportPreview(dataJson.slice(0, 5))
        Swal.fire({
          title: 'Pratinjau Berhasil',
          text: `Upload ${dataJson.length} data?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Ya, Upload'
        }).then((res) => {
          if (res.isConfirmed) handleConfirmUpload(dataJson)
          else { setImportPreview([]); if(fileInputRef.current) fileInputRef.current.value = '' }
        })
      } catch (err) { Swal.fire('Error', 'Gagal baca file', 'error') }
    }
    reader.readAsBinaryString(file)
  }

  const handleConfirmUpload = async (allData: any[]) => {
    Swal.fire({ title: 'Uploading...', didOpen: () => Swal.showLoading() })
    try {
      await importLeads(data.id as string, allData)
      Swal.fire('Berhasil', 'Data masuk antrian.', 'success')
      setImportPreview([])
      fetchLeads(1)
    } catch (error: any) { Swal.fire('Gagal', 'Error server', 'error') }
  }

  const openCallDrawer = async (lead: any, fieldName: string) => {
    setSelectedLead(lead)
    setCallNote('')
    Swal.fire({
      title: 'Connecting...',
      text: `Menghubungi ${lead.phone_number}`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    })
    try {
      await triggerCallInitiate(data.id as string, lead.id, fieldName)
      Swal.close()
      setIsDrawerOpen(true) 
    } catch(e: any) { 
      Swal.fire('Gagal Call', e.response?.data?.message || 'Pastikan Extension Anda Aktif', 'error')
    }
  }

  const handleSaveCallResult = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedLead?.id) return
    setLoading(true)
    try {
      await saveOutboundCall({
        campaignId: data.id as string,
        submissionId: selectedLead.id,
        welcomeStatusCall: valWelcomeStatusCall,
        welcomeResultCall: valWelcomeResultCall,
        welcomeStatus: valWelcomeStatus,
        productKnowledge: valWelcomeProductKnowledge,
        notes: callNote,
      })
      setValWelcomeStatusCall('')
      setValWelcomeResultCall('')
      setValWelcomeStatus('')
      setValWelcomeProductKnowledge('')
      setIsDrawerOpen(false)
      fetchLeads(currentPage) 
    } catch (err: any) {
        Swal.fire('Gagal', err.response?.data?.message || 'Gagal simpan data', 'error')
    } finally {
      setLoading(false)
    }
  }
  console.log(formField)
  const displayData = useMemo(() => {
    if (importPreview.length > 0) return importPreview
    return realData
  }, [importPreview, realData])

  return (
    <div className='d-flex flex-column gap-7 animate__animated animate__fadeIn'>
      
      {/* SCRIPT CARD */}
      <div className='card border-0 shadow-sm' style={{ borderRadius: '20px', overflow: 'hidden' }}>
        <div className='card-header border-0 py-6' style={{ background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)' }}>
          <h3 className='card-title flex-column'>
            <span className='card-label fw-bold fs-3 text-white'>Campaign Script</span>
            <span className='text-white opacity-75 mt-1 fs-7'>Gunakan narasi ini untuk nasabah</span>
          </h3>
        </div>
        <div className='card-body'>
          <div className='p-8 rounded-4 fs-6 text-gray-700 position-relative' 
               style={{ background: 'var(--bs-gray-100)', border: '1px dashed var(--bs-gray-300)' }}>
               <i className='bi bi-quote fs-1 position-absolute top-0 end-0 m-4 opacity-10'></i>
               <div className='text-gray-800' dangerouslySetInnerHTML={{__html: data?.script || '<em>No script available.</em>'}} />
          </div>
        </div>
      </div>

      {/* DATABASE CARD */}
      <div className='card border-0 shadow-sm' style={{ borderRadius: '20px' }}>
        <div className='card-header border-0 pt-7 px-8 bg-light' style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
          <div className='card-title flex-column'>
            <div className='d-flex align-items-center mb-1'>
              <span className='card-label fw-bold fs-4 text-gray-800 me-2'>
                {importPreview.length > 0 ? 'Review Import' : 'Leads Database'}
              </span>
              {loading && <span className='spinner-border spinner-border-sm text-primary'></span>}
            </div>
            <span className='text-muted fs-7'>Total {totalData} leads terdaftar.</span>
          </div>
          <div className='card-toolbar gap-3'>
            {!onTriggerProcessed && (
              <>
                <input type='file' ref={fileInputRef} onChange={handleImportLeads} className='d-none' accept='.xlsx,.xls' />
                <button className='btn btn-sm btn-secondary fw-bold px-5 shadow-sm' 
                        style={{ borderRadius: '10px' }} onClick={handleExportTemplate}>
                  <i className="bi bi-download me-2"></i>Template
                </button>
                <button className='btn btn-sm btn-primary fw-bold px-5 shadow-sm' 
                        style={{ borderRadius: '10px' }} onClick={() => fileInputRef.current?.click()}>
                  <i className="bi bi-file-earmark-arrow-up me-2"></i>Import Leads
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className='card-body p-0'>
          <div className='table-responsive'>
            <table className='table table-row-bordered table-row-gray-200 align-middle gs-9 gy-5 mb-0'>
              <thead>
                <tr className='fw-bold fs-7 text-uppercase text-gray-600 bg-light'>
                  <th className='min-w-100px sticky-column'>
                    <i className={`bi bi-shield-check me-2 ${iconColorClass}`}></i>Status
                  </th>
                  {fields.map((f, i) => (
                    <th key={i} className='min-w-150px'>
                      <i className={`bi bi-info-circle me-2 ${iconColorClass}`}></i>{f.fieldName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='fs-6'>
                {displayData.map((row, idx) => (
                  <tr key={idx} className={clsx(
                      importPreview.length > 0 && 'bg-light-success',
                      row.status_submission === 'calling' && 'bg-light-primary pulse pulse-primary'
                    )}>
                    <td className='sticky-column bg-body' style={{left: 0}}>
                      {importPreview.length > 0 ? (
                         <span className='badge badge-light-warning'>PREVIEW</span>
                      ) : (
                      <span className={clsx(
                        'badge fw-bold px-4 py-3',
                        row.status_submission === 'ready' ? 'badge-light-primary' : 
                        row.status_submission === 'calling' ? 'badge-primary' : 'badge-light-success'
                      )} style={{ borderRadius: '8px' }}>
                        {(row.status_submission || 'READY').toUpperCase()}
                      </span>
                      )}
                    </td>
                    
                    {formField.map((f: any, i: number) => {
                        const isPhoneField = f.fieldType === 'Phone';
                        const cellValue = row[f.fieldName || ''] || '-';

                        return (
                          <td key={i} className='text-gray-600 fw-medium'>
                            <div className='d-flex align-items-center'>
                              
                              {isPhoneField && f.canCall && cellValue !== '-' && (
                                <button
                                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-md me-1'
                                  style={{width: '28px', height: '28px', borderRadius: '8px'}}
                                  title={`Call ${cellValue}`}
                                  onClick={() => {
                                    const tempLead = {
                                      ...row
                                    };
                                    openCallDrawer(tempLead, f.fieldName);
                                  }}
                                >
                                  <i className='bi bi-telephone-fill fs-8 text-success'></i>
                                </button>
                              )}

                              {isPhoneField && !f.canCall && cellValue !== '-' && (
                                <i className='bi bi-telephone-x fs-8 text-gray-400 ms-2' title="Cannot call this field"></i>
                              )}
                              <span>{cellValue}</span>
                            </div>
                          </td>
                        );
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* FOOTER */}
        {!importPreview.length && totalData > 0 && (
          <div className='card-footer py-5 d-flex justify-content-between align-items-center bg-body border-0' style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}>
            <div className='text-gray-600 fs-7 fw-bold'>
              Showing {Math.min(realData.length, pageSize)} of {totalData} leads
            </div>
            <ul className='pagination pagination-outline'>
              <li className={clsx('page-item previous', currentPage === 1 && 'disabled')}>
                <button className='page-link shadow-sm border-0' onClick={() => fetchLeads(currentPage - 1)}><i className='previous'></i></button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                (i + 1 === 1 || i + 1 === totalPages || Math.abs(i + 1 - currentPage) <= 1) && (
                  <li key={i} className={clsx('page-item mx-1', currentPage === i + 1 && 'active')}>
                    <button className='page-link shadow-sm border-0 fw-bold' style={{ borderRadius: '10px' }} onClick={() => fetchLeads(i + 1)}>{i + 1}</button>
                  </li>
                )
              ))}
              <li className={clsx('page-item next', currentPage === totalPages && 'disabled')}>
                <button className='page-link shadow-sm border-0' onClick={() => fetchLeads(currentPage + 1)}><i className='next'></i></button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* DRAWER & OVERLAY */}
      <div className={clsx(
        'bg-body shadow-lg position-fixed top-0 end-0 h-100 z-index-100 d-flex flex-column', 
        isDrawerOpen ? 'animate__animated animate__slideInRight' : 'd-none'
      )} style={{ width: '100%', maxWidth: '550px', zIndex: 9999, borderTopLeftRadius: '30px', borderBottomLeftRadius: '30px' }}>
        <div className='card w-100 h-100 rounded-0 border-0' style={{ borderTopLeftRadius: '30px', borderBottomLeftRadius: '30px' }}>
          <div className='card-header pe-7 flex-grow-0 border-0 pt-8'>
            <div className='card-title'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-50px me-4'>
                    <div className='symbol-label fs-2 fw-bold bg-light-primary text-primary'>
                        {selectedLead?.full_name?.charAt(0) || 'C'}
                    </div>
                </div>
                <div className='d-flex flex-column'>
                    <span className='fs-3 fw-bolder text-gray-900'>Call Result</span>
                    <span className='text-muted fw-bold fs-7'>{selectedLead?.full_name}</span>
                </div>
              </div>
            </div>
            <div className='card-toolbar'>
              <button className='btn btn-sm btn-icon btn-light' onClick={() => setIsDrawerOpen(false)} style={{ borderRadius: '12px' }}>
                <i className='bi bi-x fs-1'></i>
              </button>
            </div>
          </div>

          <div className='card-body scroll-y px-10 pt-5 pb-10'>
            <form id='kt_call_result_form' onSubmit={handleSaveCallResult}>
              <div className='mb-10 p-6 rounded-4 border-0 position-relative overflow-hidden' 
                   style={{ background: 'var(--bs-light)', border: '1px solid var(--bs-gray-200)' }}>
                <div className='d-flex align-items-center justify-content-between'>
                    <div>
                        <h4 className='fw-bold text-gray-800 mb-1'>{selectedLead?.phone_number}</h4>
                        <span className='badge badge-success fw-bold animate__animated animate__flash animate__infinite'>
                            <span className='bullet bullet-dot bg-white me-2'></span> Active Call
                        </span>
                    </div>
                    <i className='bi bi-telephone-outbound fs-1 text-primary opacity-25'></i>
                </div>
              </div>

              <div className='row g-6'>
                {[
                    { label: 'Status Panggilan', val: valWelcomeStatusCall, set: setValWelcomeStatusCall, options: welcomeStatusCall },
                    { label: 'Hasil Panggilan', val: valWelcomeResultCall, set: setValWelcomeResultCall, options: welcomeResultCall },
                    { label: 'Status Utama', val: valWelcomeStatus, set: setValWelcomeStatus, options: welcomeStatus },
                    { label: 'Product Knowledge', val: valWelcomeProductKnowledge, set: setValWelcomeProductKnowledge, options: welcomeProductKnowledge }
                ].map((item, idx) => (
                    <div className='col-md-6 mb-2' key={idx}>
                        <label className='fs-7 fw-bold text-gray-700 mb-2 text-uppercase'>{item.label}</label>
                        <select className='form-select form-select-solid fw-bold border-0' 
                                style={{ background: 'var(--bs-gray-100)', borderRadius: '12px' }}
                                value={item.val} onChange={(e) => item.set(e.target.value)}>
                            <option value=''>Pilih...</option>
                            {item.options?.map((opt: any) => (<option key={opt.tablecode} value={opt.tablecode}>{opt.tablecode}</option>))}
                        </select>
                    </div>
                ))}
              </div>

              <div className='mt-8'>
                <label className='fs-7 fw-bold text-gray-700 mb-2 text-uppercase'>Conversation Notes</label>
                <textarea className='form-control form-control-solid fw-medium border-0' 
                          style={{ background: 'var(--bs-gray-100)', borderRadius: '15px' }}
                          rows={5} value={callNote} onChange={(e) => setCallNote(e.target.value)} 
                          placeholder='Tulis ringkasan percakapan di sini...' />
              </div>
            </form>
          </div>

          <div className='card-footer py-8 px-10 d-flex flex-stack bg-body border-0'>
            <button type='button' className='btn btn-light fw-bold px-8' 
                    style={{ borderRadius: '12px' }} onClick={() => setIsDrawerOpen(false)}>
              Batal
            </button>
            <button type='submit' form='kt_call_result_form' className='btn btn-primary fw-bold px-10 shadow-sm' 
                    style={{ borderRadius: '12px' }} disabled={loading}>
              {!loading ? (
                <><i className='bi bi-check-lg me-2'></i> Simpan Hasil</>
              ) : (
                <span className='spinner-border spinner-border-sm ms-2'></span>
              )}
            </button>
          </div>
        </div>
      </div>

      {isDrawerOpen && <div className='position-fixed top-0 start-0 w-100 h-100 bg-black opacity-25 animate__animated animate__fadeIn' style={{zIndex: 9998}} onClick={() => setIsDrawerOpen(false)} />}
      
      <style>{`
        /* Sticky Column Dark Mode Fix */
        .sticky-column { 
          position: sticky !important; 
          z-index: 1; 
          background-color: inherit;
        }
        
        /* Memberikan background solid saat scroll agar tidak transparan */
        thead th.sticky-column { background-color: var(--bs-gray-100) !important; }
        tbody td.sticky-column { background-color: var(--bs-body-bg) !important; }

        .pagination .page-item.active .page-link { background-color: #6366f1 !important; color: white !important; }
        
        /* Ratakan scrollbar untuk dark mode */
        .scroll-y::-webkit-scrollbar { width: 5px; }
        .scroll-y::-webkit-scrollbar-thumb { background: var(--bs-gray-300); border-radius: 10px; }
      `}</style>
    </div>
  )
}